import { useCallback, useEffect, useMemo } from 'react';
import { useConnectedPrescribers, useSetupProxyMutation } from '@features/eprescriptions';
import { useUserEmail } from '@features/user';
import { useEventTracking } from '@services/statsig';
import type { DoseSpotProxy } from '@store/generated/eprescriptions';

import { buildProxyOnboardingConfig } from '../configs/proxy';

import { AddonOnboardingFlow } from './AddonOnboardingFlow';

const ONBOARDING_ID = 'prescribing-proxy';

interface ProxyOnboardingProps {
  /** Called when the user finishes the flow (after the final step). */
  onComplete?: () => void;
  /** Called when the user dismisses the flow. */
  onClose?: () => void;
}

/**
 * App entry point for Prescribing Proxy onboarding. Wires the config-driven flow
 * to the real DoseSpot proxy-registration mutation (POST .../eprescriptions/setup-proxy),
 * injects the runtime data the static config can't carry, and owns the Statsig
 * tracking (kept here, not in the reusable flow, which is also rendered in Storybook):
 *
 * - `email`: the user's account email (useUserEmail) → DoseSpot payload (not a form field).
 * - welcome prescriber list: the org prescribers this proxy can prep for
 *   (useConnectedPrescribers) → welcome step bullets, omitted while loading/empty.
 *
 * The registration form's field names match the DoseSpotProxy payload, so the
 * captured values map straight through.
 */
export const ProxyOnboarding: React.FC<ProxyOnboardingProps> = ({ onComplete, onClose }) => {
  const email = useUserEmail();
  const { prescribers } = useConnectedPrescribers();
  const [setupProxy] = useSetupProxyMutation();
  const { addonOnboarding } = useEventTracking();

  // Track the modal opening once.
  useEffect(() => {
    addonOnboarding.opened({ onboarding_id: ONBOARDING_ID });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Inject the live prescriber names into the welcome step (omitted while loading/empty).
  const config = useMemo(
    () => buildProxyOnboardingConfig(prescribers.map(p => p.name)),
    [prescribers],
  );

  const handleStepChange = useCallback(
    (info: { stepIndex: number; stepKind: string; direction: string }) => {
      addonOnboarding.stepChanged({
        onboarding_id: ONBOARDING_ID,
        step_index: info.stepIndex,
        step_kind: info.stepKind,
        direction: info.direction,
      });
    },
    [addonOnboarding],
  );

  const handleSubmit = useCallback(
    async (values: Record<string, string>) => {
      addonOnboarding.submitted({ onboarding_id: ONBOARDING_ID });
      const proxy: DoseSpotProxy = {
        first_name: values.first_name,
        last_name: values.last_name,
        email,
        dob: values.dob,
        phone: values.phone,
        fax: values.fax,
        address: values.address,
        city: values.city,
        state: values.state,
        zip_code: values.zip_code,
      };
      await setupProxy({ setupProxyRequest: { proxy } }).unwrap();
    },
    [setupProxy, email, addonOnboarding],
  );

  const handleComplete = useCallback(() => {
    addonOnboarding.closed({ onboarding_id: ONBOARDING_ID, reason: 'finished' });
    onComplete?.();
  }, [onComplete, addonOnboarding]);

  const handleDismiss = useCallback(() => {
    addonOnboarding.closed({ onboarding_id: ONBOARDING_ID, reason: 'dismissed' });
    onClose?.();
  }, [onClose, addonOnboarding]);

  return (
    <AddonOnboardingFlow
      config={config}
      onSubmit={handleSubmit}
      onStepChange={handleStepChange}
      onComplete={handleComplete}
      onClose={handleDismiss}
    />
  );
};
